import { useContext, useEffect } from "react";

import { CommandMenuContext } from "@/components/command-menu/CommandMenuProvider";

import type { MenuCommand } from "@/components/command-menu/CommandMenuStore";

/**
 * Add items to the Cmd+K command menu.
 *
 * Example usage:
 * ```
 * useCommandMenu(() => ({
 *   id: "dev-tools",
 *   icon: <Icon />,
 *   name: "Developer Tools",
 *   onSelect: () => setIsDebugPanelVisible((v) => !v),
 * }), [setIsDebugPanelVisible]);
 * ```
 * The second parameter is the list of the dependencies similar to the `useEffect` dependencies. It is required
 * to update the command menu if these dependencies change.
 *
 * You can also pass the function to the `useCommandMenu` hook. This function should return the same `MenuCommand`
 * object or `undefined`.
 *
 * You can conditionally add menu items using `addCommand` function directly:
 * ```
 * const { addCommand } = useCommandMenu();
 *
 * useEffect(() => {
 *   return addCommand({
 *     id: "color-theme",
 *     icon: <Icon />,
 *     name: currentTheme === "dark" ? "Set Light Theme" : "Set Dark Theme",
 *     onSelect: () => toggleTheme(),
 *   });
 * }, [addCommand, toggleTheme, currentTheme]);
 * ```
 *
 * The return of the `addCommand` function is the callback function to remove it. You can use it
 * to conditionally remove the menu item or as a return for `useEffect` hook to clean-up on redraw.
 */
export const useCommandMenu = (
  commandOrFunction?: MenuCommand | (() => MenuCommand | undefined),
  dependencies: React.DependencyList = [],
) => {
  const store = useContext(CommandMenuContext);

  useEffect(() => {
    if (commandOrFunction) {
      const command =
        typeof commandOrFunction === "function" ? commandOrFunction() : commandOrFunction;

      if (command && !command.isDisabled) {
        return store.addCommand(command);
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, ...dependencies]);

  return store;
};
